import { UserStatus } from "../types/create-user.types";
import type { UserListItem } from "../types/users-api.types";

/**
 * "Acceso a la cuenta" — can this person sign in, and can an operator do anything about it?
 *
 * Every input is already in the listing response (`status`, `email_verified_at`, `last_login_at`,
 * `failed_login_attempts`, `locked_until`), so the whole column costs zero extra requests.
 *
 * **What this must never claim.** `two_factor_secret` is `@Exclude()`d by the API, so the client cannot tell
 * an enrolled 2FA account from one whose flag is on with no secret — the second is a permanent login lockout.
 * No state here says "2FA configured"; that inference is surfaced in the drawer, phrased as something to
 * check.
 */

export type SignInHealthKey = "suspended" | "inactive" | "locked" | "unverified" | "never" | "failing" | "ok";

export interface SignInHealth {
  key: SignInHealthKey;
  /** Minutes left on a lockout — only meaningful for `locked`. */
  lockedMinutes: number;
  /** Failed attempts so far — only meaningful for `failing`. */
  failedAttempts: number;
  /** ISO timestamp of the last sign-in — only meaningful for `ok`. */
  lastLoginAt: string | null;
}

type SignInHealthInput = Pick<
  UserListItem,
  "status" | "email_verified_at" | "last_login_at" | "failed_login_attempts" | "locked_until"
>;

function minutesUntil(iso: string | null, now: number): number {
  if (!iso) return 0;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.ceil((at - now) / 60_000));
}

/**
 * First match wins, and the order is the triage order rather than the field order.
 *
 * Lifecycle beats mechanics: a suspended account's failed attempts are irrelevant. A lockout beats an
 * unverified email because it is what is blocking the person _right now_. "Never signed in" ranks below
 * "unverified" because an unverified address is usually the reason they never did — and it is the one an
 * operator can actually fix.
 */
export function resolveSignInHealth(row: SignInHealthInput, now: number = Date.now()): SignInHealth {
  const base = {
    lockedMinutes: 0,
    failedAttempts: row.failed_login_attempts ?? 0,
    lastLoginAt: row.last_login_at,
  };

  if (row.status === UserStatus.SUSPENDED) return { ...base, key: "suspended" };
  if (row.status === UserStatus.INACTIVE) return { ...base, key: "inactive" };

  const lockedMinutes = minutesUntil(row.locked_until, now);
  if (lockedMinutes > 0) return { ...base, key: "locked", lockedMinutes };

  if (row.email_verified_at === null) return { ...base, key: "unverified" };
  if (row.last_login_at === null) return { ...base, key: "never" };
  if (base.failedAttempts > 0) return { ...base, key: "failing" };

  return { ...base, key: "ok" };
}
